import { OwnerType, TaxIdType } from '@/types/enums'
import { TAX_ID_TYPE_LABEL } from '@/utils/constants'
import { COUNTRY_LIST } from '@/utils/country-list'

export interface ShareholderEntityInput {
  id?: string
  ownership_type?: OwnerType | string
  first_name?: string | null
  last_name?: string | null
  authorized_person_first_name?: string | null
  authorized_person_last_name?: string | null
  company_name?: string | null
  email?: string | null
  phone?: string | null
  tax_id_type?: TaxIdType | string | null
  tax_id?: string | null
  address?:
    | {
        street: string
        apartment?: string
        city: string
        state: string
        zipCode: string
        country: string
      }
    | unknown
  ownership_details?:
    | {
        numberOfShares: number
        percentage: number
        signingAuthority: boolean
        boardMember: boolean
        officer: boolean
        secretary: boolean
      }
    | unknown
  created_at?: string | null
  updated_at?: string | null
}

export class ShareholderEntity {
  public id: string
  public ownership_type: OwnerType
  public first_name: string
  public last_name: string
  public authorized_person_first_name: string
  public authorized_person_last_name: string
  public company_name: string
  public email: string
  public phone: string
  public tax_id_type: TaxIdType
  public tax_id: string
  public address: {
    street: string
    apartment?: string
    city: string
    state: string
    zipCode: string
    country: string
  }
  public ownership_details: {
    numberOfShares: number
    percentage: number
    signingAuthority: boolean
    boardMember: boolean
    officer: boolean
    secretary: boolean
  }
  public created_at: string
  public updated_at: string

  constructor(shareholder: ShareholderEntityInput) {
    this.id = shareholder.id as string
    this.ownership_type = shareholder.ownership_type as OwnerType
    this.first_name = shareholder.first_name as string
    this.last_name = shareholder.last_name as string
    this.authorized_person_first_name = shareholder.authorized_person_first_name as string
    this.authorized_person_last_name = shareholder.authorized_person_last_name as string
    this.company_name = shareholder.company_name as string
    this.email = shareholder.email as string
    this.phone = shareholder.phone as string
    this.tax_id_type = shareholder.tax_id_type as TaxIdType
    this.tax_id = shareholder.tax_id as string
    this.address = shareholder.address as ShareholderEntity['address']
    this.ownership_details = shareholder.ownership_details as ShareholderEntity['ownership_details']
    this.created_at = shareholder.created_at as string
    this.updated_at = shareholder.updated_at as string
  }

  public get fullName() {
    if (this.ownership_type === OwnerType.Individual) {
      return `${this.first_name} ${this.last_name}`
    }
    return this.authorized_person_first_name + ' ' + this.authorized_person_last_name
  }

  public get taxIdLabel() {
    return TAX_ID_TYPE_LABEL[this.tax_id_type]
  }

  public get countryName() {
    const country = COUNTRY_LIST.find((x) => x.value === this.address?.country)
    return country?.label ?? this.address?.country ?? 'Unknown'
  }

  /**
   * One line for the shareholder card, laid out like the company's principal
   * address: street, apartment, city, "state postal", country. Onboarding stores
   * `street` / `zipCode`; older rows carry `street_address` / `zipcode`. Blank
   * parts are left out, never printed as empty commas.
   */
  public get fullAddress() {
    const record: Record<string, unknown> =
      this.address && typeof this.address === 'object' ? (this.address as unknown as Record<string, unknown>) : {}
    const pick = (...keys: string[]) => {
      for (const key of keys) {
        const value = record[key]
        if (typeof value === 'string' && value.trim()) return value.trim()
      }
      return ''
    }
    const state = pick('state')
    const postal = pick('zipCode', 'zipcode', 'postalCode')
    const parts = [
      pick('street', 'street_address'),
      pick('apartment'),
      pick('city'),
      [state, postal].filter(Boolean).join(' '),
      pick('country'),
    ].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : 'No address on file'
  }
}
